// OP-3605 — Regras puras do formulário de solicitação. A validação de
// verdade é do servidor (`validarCriacao`); aqui só o que evita uma ida ao
// servidor por campo vazio, e a montagem do payload com identificadores.
import { CAMPOS_OPCIONAIS_POR_TIPO, CAMPOS_POR_TIPO, NovaSolicitacaoPayload, ROTULO_CAMPO, TAMANHO_MAXIMO_OPCIONAL, TipoDemanda, UFS_BR } from '../../../../types/estoque'
import { documentoValido } from '../documento'

const TAMANHO_MAXIMO_FIXO: Record<string, number> = { cep: 9, numero: 10, contatoTelefone: 15, contatoNome: 80, contatoDocumento: 18 }

export const tamanhoMaximoDoCampo = (campo: string): number =>
  TAMANHO_MAXIMO_OPCIONAL[campo] ?? TAMANHO_MAXIMO_FIXO[campo] ?? 120

export interface EstadoForm {
  tipoDemanda: TipoDemanda
  clienteId: number | null
  tecnicoId: number | null
  localOrigemId: number | null
  eventos: number[]
  quantidades: Record<number, number>
  campos: Record<string, string>
  observacao: string
}

export const FORM_INICIAL: EstadoForm = {
  tipoDemanda: 'ENVIO', clienteId: null, tecnicoId: null, localOrigemId: null,
  eventos: [], quantidades: {}, campos: {}, observacao: '',
}

export const hojeISO = (): string => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export const camposDoTipo = (tipo: TipoDemanda): string[] => CAMPOS_POR_TIPO[tipo] || []

export const camposOpcionaisDoTipo = (tipo: TipoDemanda): string[] => CAMPOS_OPCIONAIS_POR_TIPO[tipo] || []

const OPCIONAL_DEPOIS_DE: Record<string, string> = { complemento: 'numero', pontoReferencia: 'uf' }

export function camposDoFormulario(tipo: TipoDemanda): string[] {
  const opcionais = camposOpcionaisDoTipo(tipo)
  return camposDoTipo(tipo).reduce<string[]>(
    (acc, c) => acc.concat(c, opcionais.filter((o) => OPCIONAL_DEPOIS_DE[o] === c)),
    [],
  )
}

/** O campo de data de cada tipo é o prazo do pedido — o mesmo que o servidor deriva. */
export const CAMPO_PRAZO: Record<TipoDemanda, string> = {
  INSTALACAO: 'dataInstalacao', ENVIO: 'prazoEntrega', PORTARIA: 'dataAtendimento', COLETA: 'janelaInicio',
}

export const DIAS_UTEIS_DESPACHO = 2

export const CAMPOS_COM_DESPACHO = [CAMPO_PRAZO.ENVIO, CAMPO_PRAZO.INSTALACAO]

export function despachoAte(hoje: string, diasUteis: number = DIAS_UTEIS_DESPACHO): string {
  const d = new Date(`${hoje}T00:00:00.000Z`)
  let restantes = diasUteis
  while (restantes > 0) {
    d.setUTCDate(d.getUTCDate() + 1)
    const dia = d.getUTCDay()
    if (dia !== 0 && dia !== 6) restantes -= 1
  }
  return d.toISOString().slice(0, 10)
}

export function antesDoDespacho(campo: string, valor: string | undefined, hoje: string, despacho: string = despachoAte(hoje)): boolean {
  if (CAMPOS_COM_DESPACHO.indexOf(campo) < 0 || !valor || valor < hoje) return false
  return valor <= despacho
}

export function quantidadesVisiveis(quantidades: Record<number, number>, idsVisiveis?: number[]): Record<number, number> {
  if (!idsVisiveis) return quantidades
  const visiveis = idsVisiveis.map(Number)
  return Object.keys(quantidades).reduce<Record<number, number>>((acc, id) => {
    if (visiveis.indexOf(Number(id)) >= 0) acc[Number(id)] = quantidades[Number(id)]
    return acc
  }, {})
}

export function quantidadesDoTipo(
  quantidades: Record<number, number>,
  itens: Array<{ id: number; tiposDemanda?: string | null }>,
  tipo: TipoDemanda,
): { quantidades: Record<number, number>; removidos: number[] } {
  const servem = itens.filter((i) => itemServeAoTipo(i.tiposDemanda, tipo)).map((i) => Number(i.id))
  const mantidas = quantidadesVisiveis(quantidades, servem)
  const removidos = Object.keys(quantidades).map(Number).filter((id) => !(id in mantidas) && Number(quantidades[id]) > 0)
  return { quantidades: mantidas, removidos }
}

/** Erros por campo (chave do campo, ou `_geral` para o resto). */
export function validarFormulario(f: EstadoForm, hoje: string, idsVisiveis?: number[]): Record<string, string> {
  const erros: Record<string, string> = {}
  const quantidades = quantidadesVisiveis(f.quantidades, idsVisiveis)
  if (!f.clienteId) erros.clienteId = 'Escolha o produtor.'
  if (!f.eventos.length) erros.eventos = 'Marque pelo menos um evento.'
  if (!f.localOrigemId) erros.localOrigemId = 'Escolha a praça de onde o equipamento sai.'
  if (f.tipoDemanda === 'PORTARIA' && !f.tecnicoId) erros.tecnicoId = 'Escolha o técnico responsável.'
  const itens = Object.values(quantidades).filter((q) => q > 0)
  if (!itens.length) erros.itens = 'Informe a quantidade de pelo menos um item.'
  if (Object.values(quantidades).some((q) => !Number.isInteger(q) || q < 0)) erros.itens = 'Quantidade inválida.'
  for (const c of camposDoTipo(f.tipoDemanda)) {
    const v = String(f.campos[c] ?? '').trim()
    if (!v) { erros[c] = `Preencha ${ROTULO_CAMPO[c] ?? c}.`; continue }
    if (c === 'uf' && UFS_BR.indexOf(v.toUpperCase()) < 0) erros[c] = 'UF inválida.'
    if (c === 'contatoDocumento' && !documentoValido(v)) erros[c] = 'CPF ou CNPJ inválido: confira os dígitos.'
    if (['prazoEntrega', 'dataInstalacao', 'dataAtendimento', 'janelaInicio'].indexOf(c) >= 0 && v < hoje) {
      erros[c] = 'A data não pode ficar no passado.'
    }
  }
  return erros
}

export function montarPayload(f: EstadoForm, idsVisiveis?: number[]): NovaSolicitacaoPayload {
  const campos: Record<string, string> = {}
  for (const c of camposDoTipo(f.tipoDemanda)) campos[c] = String(f.campos[c] ?? '').trim()
  for (const c of camposOpcionaisDoTipo(f.tipoDemanda)) {
    const v = String(f.campos[c] ?? '').trim()
    if (v) campos[c] = v
  }
  if (campos.uf) campos.uf = campos.uf.toUpperCase()
  return {
    tipoDemanda: f.tipoDemanda,
    clienteId: Number(f.clienteId),
    tecnicoId: f.tipoDemanda === 'PORTARIA' ? f.tecnicoId : null,
    localOrigemId: Number(f.localOrigemId),
    eventos: f.eventos.slice(),
    itens: Object.entries(quantidadesVisiveis(f.quantidades, idsVisiveis))
      .map(([itemId, quantidade]) => ({ itemId: Number(itemId), quantidade: Number(quantidade) }))
      .filter((i) => i.quantidade > 0),
    campos,
    observacao: f.observacao.trim() ? f.observacao.trim().slice(0, 1000) : null,
    prazoEm: campos[CAMPO_PRAZO[f.tipoDemanda]] || null,
  }
}

/** O item serve ao tipo? `tiposDemanda` é CSV no catálogo; vazio = serve a todos. */
export function itemServeAoTipo(tiposDemanda: string | null | undefined, tipo: TipoDemanda): boolean {
  const lista = String(tiposDemanda || '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)
  return lista.length === 0 || lista.indexOf(tipo) >= 0
}


// ---- CEP e telefone: o que o operador não deve digitar duas vezes --------

/** Só os dígitos, para comparar e para enviar ao ViaCEP. */
export const digitos = (v: string): string => String(v || '').replace(/\D/g, '')

/** `04566061` vira `04566-061`. Abaixo de 6 dígitos fica como veio. */
export function mascaraCep(v: string): string {
  const d = digitos(v).slice(0, 8)
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d
}

/**
 * Telefone brasileiro com DDD: `(21) 99985-5985` ou `(21) 3985-5985`.
 *
 * A máscara é aplicada enquanto se digita, então precisa fazer sentido em
 * cada etapa — e o nono dígito só aparece quando existe, porque fixo e
 * celular têm formatos diferentes.
 */
export function mascaraTelefone(v: string): string {
  const d = digitos(v).slice(0, 11)
  if (d.length <= 2) return d.length ? `(${d}` : ''
  const ddd = d.slice(0, 2)
  const resto = d.slice(2)
  if (resto.length <= 4) return `(${ddd}) ${resto}`
  const corte = resto.length > 8 ? 5 : 4
  return `(${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`
}

export interface EnderecoCep {
  endereco: string
  cidade: string
  uf: string
  bairro: string
}

/**
 * A resposta do ViaCEP virada nos campos do formulário.
 *
 * `erro: true` é como o serviço diz "não existe" — com HTTP 200, então não dá
 * para confiar no status. CEP de cidade inteira vem sem logradouro; nesse
 * caso a rua fica em branco para ser digitada, e cidade e UF vêm mesmo assim.
 */
export function enderecoDoCep(d: any): EnderecoCep | null {
  if (!d || d.erro) return null
  const cidade = String(d.localidade || '').trim()
  const uf = String(d.uf || '').trim().toUpperCase()
  if (!cidade || !uf) return null
  return { endereco: String(d.logradouro || '').trim(), cidade, uf, bairro: String(d.bairro || '').trim() }
}

/** Os campos que o CEP preenche: quem digita informa só o número. */
export const CAMPOS_DO_CEP = ['endereco', 'cidade', 'uf']

/**
 * OP-3619 — os passos do formulário no celular.
 *
 * O formulário é longo demais para uma rolagem só num aparelho, e as suas
 * quatro seções já são sequenciais: sem produtor não há evento, sem tipo não
 * há campo de demanda, sem praça não há item que sirva. Os passos só tornam
 * essa ordem visível — a validação continua sendo uma só, a do envio.
 */
export const PASSOS = [
  { numero: 1, titulo: 'Para quem', campos: ['clienteId', 'eventos'] },
  { numero: 2, titulo: 'O que você precisa', campos: ['localOrigemId', 'tecnicoId', 'cep', 'endereco', 'numero', 'cidade', 'uf', 'contatoNome', 'contatoTelefone', 'contatoDocumento', 'prazoEntrega', 'dataInstalacao', 'dataAtendimento', 'janelaInicio'] },
  { numero: 3, titulo: 'Equipamentos', campos: ['itens'] },
  { numero: 4, titulo: 'Conferir e enviar', campos: [] },
] as const

export const ULTIMO_PASSO = PASSOS.length

/**
 * O que impede SAIR deste passo — só os erros que nasceram nele. O passo 4 é
 * a conferência: ali o impedimento é o do formulário inteiro.
 */
export function impedimentoDoPasso(passo: number, f: EstadoForm, hoje: string, idsVisiveis?: number[]): string | null {
  const erros = validarFormulario(f, hoje, idsVisiveis)
  if (passo >= ULTIMO_PASSO) {
    const primeira = Object.keys(erros)[0]
    return primeira ? erros[primeira] : null
  }
  const doPasso = PASSOS.find((p) => p.numero === passo)
  if (!doPasso) return null
  for (const campo of doPasso.campos) {
    if (erros[campo]) return erros[campo]
  }
  return null
}
